/**
 * BrandingContext — loads tenant branding once, applies everywhere.
 *
 * Source: GET /api/platform/tenants/public/branding/{slug}
 * Works for both unauthenticated (login) and authenticated (dashboard) pages.
 * Slug is derived from the subdomain: immiways.euphoricai.io → "immiways"
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
// Phase W0 — shared host→slug resolution (handles white-label apexes like
// imiways.avanti.ai too). Dev fallback lives inside the shared helper.
import { getTenantSlug } from '../utils/tenantHost';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const DEFAULT_BRANDING = {
  name:          '',
  logo_url:      null,
  favicon_url:   null,
  primary_color: '#42a4bf',
  tagline:       null,
  // Tenant locale defaults.  Currency drives formatMoney() formatting
  // (utils/money.js); country is informational.  Backend sets these from
  // tenants.currency / tenants.country — admin can change via
  // platform admin → tenant detail → Country & Currency section.
  currency:      'USD',
  country:       null,
  // Phase W1 — white-label partner identity ({apex, platform_name, logo_url,
  // favicon_url}) for tenants served on a partner apex; null for normal
  // tenants. Drives the login page's "Powered by …" line + favicon fallback
  // so Euphoric never shows on a white-label tenant's end-client surfaces.
  white_label:   null,
  // Vertical Editions — industry key + resolved vertical config (hidden nav
  // paths, label renames, registration-flow flags). vertical stays null for
  // immigration tenants, and every consumer (utils/vertical.js) treats null
  // as "render the immigration defaults".
  industry:      'immigration',
  vertical:      null,
};

const BrandingContext = createContext(DEFAULT_BRANDING);

// Strip trailing legal-entity suffixes from a company name for display in
// browser tabs / chrome UI. Used by the document.title setter so
// "Empenhanz Ltd" reads as "Empenhanz" and "Immiways Migration Pvt Ltd"
// reads as "Immiways Migration". Iterates up to 3 times to handle compound
// suffixes ("Pvt Ltd", "Limited Liability Company"). Falls back to the
// original name if stripping would leave an empty string. Covers common
// English + EU + APAC entity suffixes — extend the regex if a tenant
// onboards with one not on the list.
const LEGAL_SUFFIX_RE = /\s+(?:pvt\.?|ltd\.?|limited|private|llp\.?|llc\.?|corp(?:oration)?\.?|inc(?:orporated)?\.?|co\.?|company|gmbh|ag|s\.?a\.?|b\.?v\.?|a\.?b\.?|a\/s|pte\.?)$/i;
function stripLegalSuffix(name) {
  if (!name) return name;
  let s = String(name).trim();
  for (let i = 0; i < 3; i++) {
    const next = s.replace(LEGAL_SUFFIX_RE, '').trim().replace(/[,.\s]+$/, '');
    if (next === s || !next) break;
    s = next;
  }
  return s || name;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    // Restore cached branding immediately to avoid flash
    const slug = getTenantSlug();
    if (!slug) return DEFAULT_BRANDING;
    try {
      const cached = localStorage.getItem(`branding_${slug}`);
      if (cached) return { ...DEFAULT_BRANDING, ...JSON.parse(cached) };
    } catch {}
    return DEFAULT_BRANDING;
  });

  // Single source of truth for fetching branding. Used both on mount AND by
  // the Settings page after a logo/favicon upload. Cache prevention is done
  // SERVER-SIDE via Cache-Control: no-store on /public/branding/{slug}
  // (see backend/routers/platform/tenants.py). We do NOT send a
  // Cache-Control request header here — that triggers a CORS preflight
  // (Cache-Control is not a CORS-safelisted request header), and a failing
  // preflight silently blocks the GET and leaves stale data in place
  // (the symptom user hit on 2026-06-02: dashboard rendered USD forever
  // even after platform DB had INR). The `?_=Date.now()` cache-buster
  // already defeats any URL-keyed cache; that's enough.
  const refresh = useCallback(async () => {
    const slug = getTenantSlug();
    if (!slug) return;
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/platform/tenants/public/branding/${slug}?_=${Date.now()}`,
      );
      const data = { ...DEFAULT_BRANDING, ...r.data };
      setBranding(data);
      try { localStorage.setItem(`branding_${slug}`, JSON.stringify(data)); } catch {}
    } catch {
      // On error, clear the cached entry so the next mount tries fresh
      // rather than silently keeping the user stuck on a stale value.
      try { localStorage.removeItem(`branding_${slug}`); } catch {}
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Set the browser tab title to "<brand-name> | Case Management Platform"
  // whenever branding.name changes. Previously the index.html had a
  // hardcoded "Immiways | Case Management Platform" that followed every
  // tenant around — surfaced 2026-06-02 with Empenhanz tab showing
  // "Immiways". Build-time title now reads just "Case Management Platform"
  // and we own this dynamically.
  //
  // brand-name = company name minus legal entity suffix (Ltd, Pvt Ltd,
  // LLC, Inc., GmbH, etc.). Salesforce/HubSpot use the brand name, not
  // the legal entity name, in browser tabs and chrome UI. "Empenhanz Ltd"
  // → "Empenhanz", "Immiways Migration Pvt Ltd" → "Immiways Migration".
  useEffect(() => {
    if (branding?.name) {
      document.title = `${stripLegalSuffix(branding.name)} | Case Management Platform`;
    }
  }, [branding?.name]);

  // Apply the tenant's favicon to <link rel="icon"> whenever it changes.
  // Targets every existing icon link so we replace the build-time default
  // (favicon.ico) rather than appending alongside it. Falls back gracefully
  // when no favicon is set — the build-time default stays.
  useEffect(() => {
    // Tenant favicon first; a white-label tenant without one falls back to
    // the PARTNER's favicon (never the build-time Euphoric default).
    const effectiveFavicon = branding.favicon_url || branding.white_label?.favicon_url;
    if (!effectiveFavicon) return;
    const heads = document.head.querySelectorAll("link[rel*='icon']");
    if (heads.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = effectiveFavicon;
      document.head.appendChild(link);
      return;
    }
    heads.forEach((el) => { el.href = effectiveFavicon; });
  }, [branding.favicon_url, branding.white_label]);

  // Expose `refresh` alongside the branding fields so any consumer (e.g. the
  // Settings page after an upload) can force a re-pull without remounting.
  const value = { ...branding, refresh };
  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Convenience hook: returns just the tenant's currency code (e.g. 'INR').
 * Use with formatMoney(amount, useTenantCurrency()) so every money figure
 * across the tenant UI follows the tenant's chosen currency.
 */
export function useTenantCurrency() {
  return useContext(BrandingContext).currency || 'USD';
}

/**
 * TenantLogo — renders the tenant logo or company name as text fallback.
 * Never shows a hardcoded Immiways logo.
 */
export function TenantLogo({ className = 'h-24 object-contain', textClassName = '' }) {
  const { logo_url, name, primary_color } = useBranding();

  if (logo_url) {
    return <img src={logo_url} alt={name || 'Logo'} className={className} />;
  }

  if (name) {
    return (
      <span
        style={{ color: primary_color, fontWeight: 700, fontSize: 22, letterSpacing: 1 }}
        className={textClassName}
      >
        {name.toUpperCase()}
      </span>
    );
  }

  return null;
}
